import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Button, Tag, Card, ProgressBar, Alert, Icon, Title, Text,
} from '@nimbus-ds/components'
import {
  ArrowLeftIcon,
  StatsIcon,
  FlagIcon,
  ClockIcon,
  CheckCircleIcon,
  StopIcon,
  ExclamationCircleIcon,
} from '@nimbus-ds/icons'
import { useToast } from '@/hooks/use-toast'
import type { Experiment, ExperimentResults } from '@/types'
import { experimentService } from '@/api/experiments'
import { FunnelSankeyChart } from '@/components/analytics/FunnelSankeyChart'
import { FunnelMetricsCard } from '@/components/analytics/FunnelMetricsCard'
import { DevicePerformanceCard } from '@/components/experiments/DevicePerformanceCard'
import { CustomEventsAnalytics } from '@/components/analytics/CustomEventsAnalytics'
import { DecisionHelper } from '@/components/analytics/DecisionHelper'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

// Legacy primary_metric → label map, used only for experiments created before the
// goals consolidation. New experiments always have goals.primary.label set.
const PRIMARY_METRIC_LABELS: Record<string, string> = {
  purchase_rate: 'Tasa de compra',
  add_to_cart_rate: 'Tasa de agregar al carrito',
  view_cart_rate: 'Tasa de vista de carrito',
  checkout_rate: 'Tasa de inicio de checkout',
  conversion_rate: 'Tasa de compra',
}

function goalLabelFromExperiment(experiment: Experiment | null): string | null {
  if (!experiment) return null
  if (experiment.goals?.primary?.label) return experiment.goals.primary.label
  if (experiment.primary_metric) return PRIMARY_METRIC_LABELS[experiment.primary_metric] || experiment.primary_metric
  return null
}

export function ExperimentResultsPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [experiment, setExperiment] = useState<Experiment | null>(null)
  const [results, setResults] = useState<ExperimentResults | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (id) {
      loadData()
    }
  }, [id])

  const loadData = async () => {
    try {
      setLoading(true)
      if (id) {
        const [experimentData, resultsData] = await Promise.allSettled([
          experimentService.getExperiment(id),
          experimentService.getExperimentResults(id),
        ])

        if (experimentData.status === 'fulfilled') {
          setExperiment(experimentData.value)
        }

        if (resultsData.status === 'fulfilled') {
          setResults(resultsData.value)
        } else {
          // Results might not exist yet, that's ok
          console.log('Results not available yet')
        }
      }
    } catch (error) {
      console.error('Failed to load experiment data:', error)
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos del experimento.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 rounded bg-muted mb-2"></div>
          <div className="h-4 w-64 rounded bg-muted"></div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <Card.Header>
                <div className="h-4 w-20 rounded bg-muted"></div>
              </Card.Header>
              <Card.Body>
                <div className="h-8 w-16 rounded bg-muted"></div>
              </Card.Body>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="mb-3">
          <Button
            appearance="transparent"
            onClick={() => navigate(`/dashboard/experiments/${id}`)}
          >
            <Icon source={<ArrowLeftIcon />} color="currentColor" />
            Volver a detalles
          </Button>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <span className="shrink-0 text-muted-foreground"><Icon source={<StatsIcon />} /></span>
            <div className="min-w-0">
              <Title as="h1" className="tracking-tight">Resultados del Experimento</Title>
              <Text className="truncate">
                {experiment?.name || `Experimento ${id}`}
              </Text>
              {goalLabelFromExperiment(experiment) && (
                <Text as="p" className="mt-1">
                  <Text as="span">Objetivo principal: </Text>
                  <Text as="span" fontWeight="medium" color="neutral-textHigh">{goalLabelFromExperiment(experiment)}</Text>
                </Text>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
            {(experiment?.is_negative || results?.is_negative) && (
              <div className="shrink-0 whitespace-nowrap" title="Control y tratamiento están invertidos en los cálculos">
                <Tag appearance="neutral">Test negativo</Tag>
              </div>
            )}
            {experiment && (
              <div className="shrink-0 whitespace-nowrap">
                <Tag
                  appearance={
                    experiment.status === 'active' ? 'primary' :
                    experiment.status === 'completed' ? 'success' :
                    experiment.status === 'paused' ? 'warning' : 'neutral'
                  }
                >
                  {experiment.status === 'active' && <Icon source={<ClockIcon />} color="currentColor" />}
                  {experiment.status === 'completed' && <Icon source={<CheckCircleIcon />} color="currentColor" />}
                  {experiment.status === 'paused' && <Icon source={<StopIcon />} color="currentColor" />}
                  {experiment.status === 'draft' && <Icon source={<ExclamationCircleIcon />} color="currentColor" />}
                  {experiment.status === 'active' ? 'En ejecución' :
                   experiment.status === 'completed' ? 'Completado' :
                   experiment.status === 'paused' ? 'Pausado' : 'Borrador'}
                </Tag>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 1. VERDICT — first answer: how is it going + what to do */}
      <DecisionHelper experiment={experiment} results={results} />

      {/* 2. HEALTH STRIP — compact: data collection + data quality */}
      {experiment && (() => {
        const isCompleted = experiment.status === 'completed' || experiment.status === 'archived'
        const startDate = experiment.timing?.start_date_time || experiment.created_at
        const endDate = experiment.timing?.end_date_time || null
        const now = new Date()
        const start = startDate ? new Date(startDate) : now
        const daysRunning = results?.duration?.days_running ?? Math.max(0, Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)))
        const totalSessions = results?.summary?.total_sessions || experiment.current_sessions || 0

        let daysRemaining: number | null = null
        if (endDate && !isCompleted) {
          const end = new Date(endDate)
          daysRemaining = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
        }

        let sessionProgress: number | null = null
        if (experiment.session_limit) {
          sessionProgress = Math.min(100, (totalSessions / experiment.session_limit) * 100)
        }

        const srmValid = results?.srm_analysis?.is_valid
        const dateRange = `${start.toLocaleDateString('es-ES', { dateStyle: 'medium' })}${endDate ? ` – ${new Date(endDate).toLocaleDateString('es-ES', { dateStyle: 'medium' })}` : ''}`

        return (
          <Card>
            <Card.Header>
              <div className="flex items-center gap-2">
                <Icon source={<StatsIcon />} />
                <Title as="h4">Salud del Experimento</Title>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {/* Days */}
                <div>
                  <Text fontSize="caption">
                    {isCompleted ? 'Duración total' : 'Días corriendo'}
                  </Text>
                  <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{daysRunning}</Text>
                  <Text fontSize="caption" className="mt-0.5">{dateRange}</Text>
                  {!isCompleted && daysRemaining !== null && (
                    <p className={`text-xs ${daysRemaining === 0 ? 'text-red-600' : daysRemaining <= 3 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                      {daysRemaining === 0 ? 'Plazo vencido' : `${daysRemaining} días restantes`}
                    </p>
                  )}
                </div>

                {/* Sessions */}
                <div>
                  <Text fontSize="caption">Sesiones</Text>
                  <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{totalSessions.toLocaleString('es-ES')}</Text>
                  {experiment.session_limit && (
                    <div className="mt-1.5 space-y-1">
                      <ProgressBar value={sessionProgress ?? 0} appearance="primary" />
                      <Text fontSize="caption">
                        {sessionProgress?.toFixed(0)}% de {experiment.session_limit.toLocaleString('es-ES')}
                      </Text>
                    </div>
                  )}
                </div>

                {/* SRM */}
                <div>
                  <Text fontSize="caption">Distribución (SRM)</Text>
                  {srmValid === undefined ? (
                    <Text fontSize="highlight" fontWeight="bold">—</Text>
                  ) : (
                    <p className={`text-xl font-bold ${srmValid ? 'text-green-600' : 'text-red-600'}`}>
                      {srmValid ? 'Válida' : 'Sesgada'}
                    </p>
                  )}
                  {results?.srm_analysis?.warning && (
                    <p className="mt-0.5 text-xs text-amber-600 line-clamp-2">{results.srm_analysis.warning}</p>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        )
      })()}

      {results && results.sessions && results.conversion_rate ? (
        <>
          {/* Goal Banner */}
          {results.goal_definition && results.goal_definition.primary.type !== 'purchase' && (
            <Alert title="Objetivo principal" appearance="primary">
              <span className="flex items-center gap-2">
                <Icon source={<FlagIcon />} />
                <span>
                  {results.goal_definition.primary.label}
                  {' — '}Las métricas principales reflejan este objetivo. Las compras se rastrean por separado.
                </span>
              </span>
            </Alert>
          )}

          {/* === 3. RENDIMIENTO === */}
          <div className="flex items-center gap-3 pt-2">
            <Title as="h2" className="tracking-tight">Rendimiento</Title>
            <div className="h-px flex-1 bg-border" />
          </div>

          {/* Comparison Table — Baseline vs Hipótesis with delta.
              For negative experiments control IS the hypothesis variant, so we swap
              roles to keep "left column = baseline, right column = hypothesis, Δ in
              favor of hypothesis" reading order. */}
          {(() => {
            const isNegative = Boolean(experiment?.is_negative || results?.is_negative)
            const hypKey: 'control' | 'treatment' = isNegative ? 'control' : 'treatment'
            const baseKey: 'control' | 'treatment' = isNegative ? 'treatment' : 'control'
            const hypHeader = isNegative ? 'Control (Hipótesis)' : 'Tratamiento'
            const baseHeader = isNegative ? 'Tratamiento (Baseline)' : 'Control'

            const baseSessions = results.sessions?.[baseKey] || 0
            const hypSessions = results.sessions?.[hypKey] || 0
            const baseConv = results.conversions?.[baseKey] || 0
            const hypConv = results.conversions?.[hypKey] || 0
            const baseRate = (results.conversion_rate?.[baseKey] || 0) * 100
            const hypRate = (results.conversion_rate?.[hypKey] || 0) * 100

            const includePurchase = results.goal_metrics && results.goal_definition?.primary?.type !== 'purchase'
            const basePurchases = includePurchase ? results.goal_metrics![baseKey].purchase_conversions : null
            const hypPurchases = includePurchase ? results.goal_metrics![hypKey].purchase_conversions : null
            const basePurchRate = includePurchase ? results.goal_metrics![baseKey].purchase_conversion_rate * 100 : null
            const hypPurchRate = includePurchase ? results.goal_metrics![hypKey].purchase_conversion_rate * 100 : null

            // Delta = hypothesis vs baseline. Positive = hypothesis better = green.
            const formatDelta = (base: number, hyp: number, isPercentagePoints = false) => {
              if (base === 0) return { text: '—', tone: 'muted' as const }
              const diff = hyp - base
              if (isPercentagePoints) {
                const sign = diff > 0 ? '+' : ''
                return {
                  text: `${sign}${diff.toFixed(2)}pp`,
                  tone: diff > 0 ? ('up' as const) : diff < 0 ? ('down' as const) : ('muted' as const),
                }
              }
              const pct = (diff / base) * 100
              const sign = pct > 0 ? '+' : ''
              return {
                text: `${sign}${pct.toFixed(1)}%`,
                tone: pct > 0 ? ('up' as const) : pct < 0 ? ('down' as const) : ('muted' as const),
              }
            }

            const toneClass = (tone: 'up' | 'down' | 'muted') =>
              tone === 'up' ? 'text-green-600' : tone === 'down' ? 'text-red-600' : 'text-muted-foreground'

            const goalLabel = goalLabelFromExperiment(experiment)

            const rows: Array<{
              label: string
              hint?: string
              baseline: string
              hypothesis: string
              delta: ReturnType<typeof formatDelta>
              emphasis?: boolean
            }> = [
              {
                label: 'Sesiones',
                baseline: baseSessions.toLocaleString('es-ES'),
                hypothesis: hypSessions.toLocaleString('es-ES'),
                delta: formatDelta(baseSessions, hypSessions),
              },
              {
                label: goalLabel ? `Conversiones (${goalLabel})` : 'Conversiones',
                hint: 'Objetivo principal del experimento',
                baseline: baseConv.toLocaleString('es-ES'),
                hypothesis: hypConv.toLocaleString('es-ES'),
                delta: formatDelta(baseConv, hypConv),
              },
              {
                label: goalLabel ? `Tasa de conversión (${goalLabel})` : 'Tasa de conversión',
                hint: 'Objetivo principal del experimento',
                baseline: `${baseRate.toFixed(2)}%`,
                hypothesis: `${hypRate.toFixed(2)}%`,
                delta: formatDelta(baseRate, hypRate, true),
                emphasis: true,
              },
            ]

            if (includePurchase) {
              rows.push(
                {
                  label: 'Compras',
                  hint: 'Rastreadas independientemente del objetivo',
                  baseline: (basePurchases ?? 0).toLocaleString('es-ES'),
                  hypothesis: (hypPurchases ?? 0).toLocaleString('es-ES'),
                  delta: formatDelta(basePurchases ?? 0, hypPurchases ?? 0),
                },
                {
                  label: 'Tasa de compra',
                  baseline: `${(basePurchRate ?? 0).toFixed(2)}%`,
                  hypothesis: `${(hypPurchRate ?? 0).toFixed(2)}%`,
                  delta: formatDelta(basePurchRate ?? 0, hypPurchRate ?? 0, true),
                }
              )
            }

            return (
              <Card padding="none">
                <div>
                  {/* Header row */}
                  <div className="grid grid-cols-[1.5fr_1fr_1fr_minmax(0,0.8fr)] items-center gap-2 border-b bg-muted/30 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:gap-4 sm:px-6">
                    <div>Métrica</div>
                    <div className="text-right">{baseHeader}</div>
                    <div className="text-right">{hypHeader}</div>
                    <div className="text-right">Δ</div>
                  </div>
                  {/* Rows */}
                  {rows.map((row, i) => (
                    <div
                      key={i}
                      className={`grid grid-cols-[1.5fr_1fr_1fr_minmax(0,0.8fr)] items-baseline gap-2 px-4 py-3 sm:gap-4 sm:px-6 ${
                        i < rows.length - 1 ? 'border-b' : ''
                      } ${row.emphasis ? 'bg-muted/20' : ''}`}
                    >
                      <div className="min-w-0">
                        <Text fontWeight={row.emphasis ? 'bold' : 'medium'} color="neutral-textHigh">{row.label}</Text>
                        {row.hint && (
                          <Text fontSize="caption" className="truncate">{row.hint}</Text>
                        )}
                      </div>
                      <div className={`text-right tabular-nums ${row.emphasis ? 'text-xl font-bold sm:text-2xl' : 'text-base font-semibold sm:text-lg'}`}>
                        {row.baseline}
                      </div>
                      <div className={`text-right tabular-nums ${row.emphasis ? 'text-xl font-bold sm:text-2xl' : 'text-base font-semibold sm:text-lg'}`}>
                        {row.hypothesis}
                      </div>
                      <div className={`text-right text-sm font-semibold tabular-nums sm:text-base ${toneClass(row.delta.tone)}`}>
                        {row.delta.text}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )
          })()}

          {/* Technical detail — supporting numbers, not the headline. The
              headline "Confianza" (BB+ROPE) lives on the DecisionHelper card
              above; showing a second "Confianza" tile here (z-test, always a
              different number) confused merchants. Kept Lift + p-value + chi²
              for consultants; the drill-in for both engines lives in the
              tooltip on the headline. */}
          <Card>
            <Card.Header>
              <div className="flex items-center gap-2">
                <Icon source={<StatsIcon />} />
                <Title as="h4">Detalles técnicos</Title>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div>
                  <Text fontSize="caption" fontWeight="medium">
                    Lift{(experiment?.is_negative || results?.is_negative) ? ' (hipótesis)' : ''}
                  </Text>
                  {(() => {
                    const isNegative = Boolean(experiment?.is_negative || results?.is_negative)
                    // For negative experiments lift is framed as hypothesis-vs-baseline:
                    //   (control_rate - treatment_rate) / treatment_rate
                    // For normal experiments we use the lambda-provided value:
                    //   (treatment_rate - control_rate) / control_rate
                    let displayLift = results.lift * 100
                    if (isNegative) {
                      const ctrlRate = results.conversion_rate?.control ?? 0
                      const trtRate = results.conversion_rate?.treatment ?? 0
                      displayLift = trtRate > 0 ? ((ctrlRate - trtRate) / trtRate) * 100 : -displayLift
                    }
                    return (
                      <p className={`text-2xl font-bold ${displayLift > 0 ? 'text-green-600' : displayLift < 0 ? 'text-red-600' : ''}`}>
                        {displayLift > 0 ? '+' : ''}{displayLift.toFixed(2)}%
                      </p>
                    )
                  })()}
                </div>
                <div>
                  <Text fontSize="caption" fontWeight="medium">P-Value</Text>
                  <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{results.p_value.toFixed(4)}</Text>
                </div>
                <div>
                  <Text fontSize="caption" fontWeight="medium">Chi-Square</Text>
                  <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{results.srm_analysis.chi_square.toFixed(2)}</Text>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Timeline — daily evolution of conversion rate per variant */}
          {results.time_series && results.time_series.length > 0 && (
            <Card>
              <Card.Header>
                <div className="flex items-center gap-2">
                  <Icon source={<StatsIcon />} />
                  <Title as="h4">Evolución Diaria</Title>
                </div>
              </Card.Header>
              <Card.Body>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart
                    data={results.time_series.map(d => ({
                      label: new Date(d.date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' }),
                      date: d.date,
                      control: Number((d.control_rate * 100).toFixed(2)),
                      treatment: Number((d.treatment_rate * 100).toFixed(2)),
                    }))}
                    margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="label" className="text-xs" />
                    <YAxis className="text-xs" unit="%" />
                    <RechartsTooltip
                      formatter={(value: any, name: any) => [`${Number(value).toFixed(2)}%`, name === 'control' ? 'Control' : 'Tratamiento']}
                      labelFormatter={(_label, payload: any) =>
                        payload?.[0]?.payload?.date
                          ? new Date(payload[0].payload.date).toLocaleDateString('es-ES')
                          : ''
                      }
                    />
                    <Legend formatter={(v) => (v === 'control' ? 'Control' : 'Tratamiento')} />
                    <Line type="monotone" dataKey="control" stroke="#6b7280" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="treatment" stroke="#2563eb" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </Card.Body>
            </Card>
          )}

          {/* === 4. ANÁLISIS DETALLADO === drill-down */}
          {(
            (results.by_device?.present) ||
            (results.funnel_analysis && results.funnel_analysis.length > 0) ||
            (results.goal_metrics?.secondary_goals && results.goal_metrics.secondary_goals.length > 0) ||
            (results.custom_events_comparison && results.custom_events_comparison.length > 0) ||
            (results.variants?.control?.events && Object.keys(results.variants.control.events).length > 0)
          ) && (
            <div className="flex items-center gap-3 pt-4">
              <Title as="h2" className="tracking-tight">Análisis Detallado</Title>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          {/* Performance by device — segmentation + SRM-by-device + Simpson's (§8) */}
          {results.by_device?.present && (
            <DevicePerformanceCard
              analysis={results.by_device}
              isNegative={Boolean(experiment?.is_negative || results?.is_negative)}
            />
          )}

          {/* Funnel — primary drill-down */}
          {results.funnel_analysis && results.funnel_analysis.length > 0 && (
            <div className="space-y-4">
              <Title as="h3">Funnel de Conversión</Title>
              <FunnelSankeyChart
                funnelData={results.funnel_analysis}
                variant="both"
              />
              <FunnelMetricsCard
                funnelData={results.funnel_analysis}
                isNegative={Boolean(experiment?.is_negative || results?.is_negative)}
              />
            </div>
          )}

          {/* Secondary Goals */}
          {results.goal_metrics?.secondary_goals && results.goal_metrics.secondary_goals.length > 0 && (
            <Card>
              <Card.Header title="Objetivos Secundarios" />
              <Card.Body>
                <div className="space-y-2">
                  {results.goal_metrics.secondary_goals.map((sg, i) => (
                    <div key={i} className="grid grid-cols-1 gap-1 border-b pb-2 last:border-0 sm:grid-cols-3 sm:gap-4">
                      <div className="font-medium text-sm">{sg.label}</div>
                      <div className="text-sm text-muted-foreground">
                        Control: {sg.control.conversions} ({(sg.control.conversion_rate * 100).toFixed(2)}%)
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Tratamiento: {sg.treatment.conversions} ({(sg.treatment.conversion_rate * 100).toFixed(2)}%)
                      </div>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Custom Events */}
          {((results.custom_events_comparison && results.custom_events_comparison.length > 0) ||
            (results.variants?.control?.events && Object.keys(results.variants.control.events).length > 0)) && (
            <div className="space-y-4">
              <Title as="h3">Eventos Personalizados</Title>
              <div>
                <CustomEventsAnalytics
                  events={results.custom_events_comparison
                    ? results.custom_events_comparison.map(ce => ({
                        event_name: ce.display_name,
                        control: {
                          count: ce.control.total_occurrences,
                          unique_sessions: ce.control.unique_sessions,
                          rate: ce.control.rate * 100,
                        },
                        treatment: {
                          count: ce.treatment.total_occurrences,
                          unique_sessions: ce.treatment.unique_sessions,
                          rate: ce.treatment.rate * 100,
                        },
                        is_significant: ce.is_significant,
                        p_value: ce.p_value,
                        lift_percentage: ce.lift_percentage,
                        confidence_level: ce.confidence_level,
                      }))
                    : Object.entries(results.variants!.control!.events!).map(([eventKey, controlEvent]) => ({
                        event_name: controlEvent.display_name,
                        event_type: controlEvent.category,
                        control: {
                          count: controlEvent.total_occurrences,
                          unique_sessions: controlEvent.unique_visitors || 0,
                          rate: controlEvent.engagement_rate,
                        },
                        treatment: {
                          count: results.variants?.treatment?.events?.[eventKey]?.total_occurrences || 0,
                          unique_sessions: results.variants?.treatment?.events?.[eventKey]?.unique_visitors || 0,
                          rate: results.variants?.treatment?.events?.[eventKey]?.engagement_rate || 0,
                        },
                      }))
                  }
                  totalSessions={{
                    control: results.sessions.control,
                    treatment: results.sessions.treatment,
                  }}
                />
              </div>
            </div>
          )}
        </>
      ) : (
        <Card>
          <Card.Header>
            <Title as="h3">Sin Datos Disponibles</Title>
            <Text>
              No hay suficientes datos para mostrar resultados estadísticamente significativos.
            </Text>
          </Card.Header>
          <Card.Body>
            <Text className="mb-4">
              Los resultados aparecerán cuando el experimento tenga suficiente tráfico y conversiones.
              Esto puede tomar desde unas horas hasta varios días dependiendo del volumen de tu tienda.
            </Text>
            <Text>
              Estado del experimento: <Text as="span" fontWeight="medium" color="neutral-textHigh">
                {experiment?.status === 'active' ? 'Activo - recolectando datos' :
                 experiment?.status === 'draft' ? 'Borrador - no iniciado' :
                 experiment?.status === 'paused' ? 'Pausado' : 'Completado'}
              </Text>
            </Text>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}
